import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Warning } from '@phosphor-icons/react'
import { Trans, useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import { selectUserRole } from '../../reducers/userDataReducer'
import ModalContainer from '../ModalContainer/ModelContainer'

import './AccountWarningModal.scss'

const getStorageKey = (buildingId, type) => `account_${type}_dismissed_${buildingId}`

const ROLES_WITH_WARNING = ['ShiftManager', 'PropertyManager', 'Staff']

const AccountWarningModal = () => {
  const { t } = useTranslation(['account-warning-modal'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const userRole = useSelector(selectUserRole)
  const [isDismissed, setIsDismissed] = useState(true)

  const buildingId = activeBuilding?.id
  const accountPastDue = activeBuilding?.accountPastDue ?? false
  const accountSuspended = activeBuilding?.accountSuspended ?? false

  useEffect(() => {
    if (!buildingId) return

    // Check if already dismissed for this building this session
    if (accountSuspended) {
      // Suspended modal can never be dismissed
      setIsDismissed(false)
    } else if (accountPastDue) {
      const wasDismissed =
        window.sessionStorage.getItem(getStorageKey(buildingId, 'warning')) === 'true'
      setIsDismissed(wasDismissed)
    } else {
      setIsDismissed(true)
    }
  }, [buildingId, accountPastDue, accountSuspended])

  const handleDismiss = () => {
    if (buildingId && !accountSuspended) {
      window.sessionStorage.setItem(getStorageKey(buildingId, 'warning'), 'true')
      setIsDismissed(true)
    }
  }

  // Only show warnings for front desk, property manager, and maintenance roles
  if (!ROLES_WITH_WARNING.includes(userRole)) return null

  // Don't show if no building selected or already dismissed
  if (!activeBuilding || isDismissed) return null

  // Determine which message to show
  const isSuspended = accountSuspended

  return (
    <ModalContainer
      isVisible={true}
      headerTitle={
        isSuspended
          ? t('account-warning-modal:suspended.title')
          : t('account-warning-modal:pastDue.title')
      }
      headerIcon={<Warning size={24} color={isSuspended ? '#DC2626' : '#D97706'} weight="fill" />}
      closeable={!isSuspended}
      onClose={handleDismiss}
      additionalClass={`account-warning-modal ${isSuspended ? 'account-warning-modal--suspended' : ''}`}
    >
      <div className="account-warning-modal__content">
        {isSuspended ? (
          <>
            <p className="account-warning-modal__message">
              {t('account-warning-modal:suspended.message')}
            </p>
            <p className="account-warning-modal__contact">
              <Trans
                t={t}
                i18nKey="account-warning-modal:shared.contactMessage"
                components={[
                  <a key="email" href="mailto:felipe.valencia@elevatedliving.com" />,
                  <a key="phone" href="tel:+19543726069" />,
                ]}
              />
            </p>
          </>
        ) : (
          <>
            <p className="account-warning-modal__message">
              {t('account-warning-modal:pastDue.message')}
            </p>
            <p className="account-warning-modal__contact">
              <Trans
                t={t}
                i18nKey="account-warning-modal:shared.contactMessage"
                components={[
                  <a key="email" href="mailto:felipe.valencia@elevatedliving.com" />,
                  <a key="phone" href="tel:+19543726069" />,
                ]}
              />
            </p>
            <div className="account-warning-modal__actions">
              <button className="button button-primary" onClick={handleDismiss}>
                {t('account-warning-modal:pastDue.dismissButton')}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalContainer>
  )
}

export default AccountWarningModal
